import {Component, HostListener, Injector, OnInit, ViewChild} from '@angular/core';
import {BaseComponent} from '../base/base.component';
import {Invoice} from '../../models/invoice';
import {Permissions} from '../../resources/permissions';
import Swal from 'sweetalert2';
import {InvoiceService} from '../../services/invoice/invoice.service';
import {CompanyService} from '../../services/company/company.service';
import * as moment from 'moment';
import 'moment/locale/it';
import {Company} from '../../models/company';
import {OverlayPanel} from 'primeng/overlaypanel';
import {LazyLoadEvent} from 'primeng/api';

interface InvoicePeriodTransaction {
    id: string;
    kind: 'enrollment' | 'order';
    date: string;
    label: string;
    detail: string;
    amount: number;
    feeAmount: number;
    feeRate: number | null;
    hasFeeSnapshot: boolean;
    paymentStatus?: string;
    paymentMethod?: string;
    feeZeroReason: 'importo-zero' | 'pending' | 'zero-rate' | 'none' | null;
    installmentNumber?: number;
    totalInstallments?: number;
}

interface InvoicePeriodOverview {
    periodStart: string;
    periodEnd: string;
    projected: boolean;
    subscriptionAmount: number | null;
    feeAmount: number | null;
    totalDue: number | null;
    nextStripePayment: {
        amountDue: number;
        planAmount: number | null;
        feeAmount: number | null;
        otherAmount: number | null;
        currency: string;
        expectedAt: string;
    } | null;
    nextStripePaymentStatus: 'not_requested' | 'available' | 'none' | 'unavailable' | 'plan_missing';
    feeRate: number | null;
    incomeAmount: number;
    invoice?: {id: string; number: string; paymentStatus: string; url?: string} | null;
    transactions: InvoicePeriodTransaction[];
}

@Component({
    selector: 'app-invoices-list',
    templateUrl: './invoices-list.component.html',
    styleUrls: ['./invoices-list.component.scss']
})
export class InvoicesListComponent extends BaseComponent implements OnInit {
    @ViewChild('filtersPanel') filtersPanel?: OverlayPanel;

    invoices: Invoice[] = [];
    totalRecords = 0;
    loading = true;
    first = 0;
    rows = 30;
    skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

    userIsAdmin = false;
    userIsAgent = false;
    canFilterCompanies = false;
    overviewLoading = false;
    overview: InvoicePeriodOverview | null = null;
    activeCompanyTab: 'income' | 'welldone' = 'income';
    feeHelpOpen = false;
    incomeRowsVisible = 25;
    private overviewRequestId = 0;

    textFilter = '';
    selectedPeriod: string | null = null;
    selectedCompanyIds: string[] = [];
    selectedStatusIds: string[] = [];
    selectedMethodIds: string[] = [];
    sortBy = 'createdAt desc';
    companies: Company[] = [];
    periodOptions: {id: string; name: string}[] = [];

    readonly statusOptions = [
        {id: 'paid', name: 'Pagata'},
        {id: 'notPaid', name: 'Da pagare'},
        {id: 'waitingPayment', name: 'In attesa'},
    ];
    readonly methodOptions = [
        {id: 'transfer', name: 'Bonifico bancario'},
        {id: 'nexi', name: 'Carta di credito Nexi'},
        {id: 'nexiRates', name: 'Rate mensili Nexi'},
        {id: 'stripe', name: 'Stripe'},
        {id: 'appPagoRates', name: 'App / PagoPA'},
        {id: 'localPayment', name: 'Pagamento in sede'},
        {id: 'soisy', name: 'Soisy'},
    ];
    readonly sortOptions = [
        {id: 'createdAt desc', name: 'Più recenti'},
        {id: 'createdAt asc', name: 'Più vecchie'},
        {id: 'date desc', name: 'Data fattura'},
        {id: 'amount desc', name: 'Importo più alto'},
        {id: 'amount asc', name: 'Importo più basso'},
        {id: 'number desc', name: 'Numero'},
    ];

    regeneratingInvoiceId?: string;
    detailOpen = false;
    selectedInvoice: Invoice | null = null;
    copiedKey = '';
    private copiedTimer: ReturnType<typeof setTimeout> | null = null;
    private lastLazyEvent: LazyLoadEvent = {first: 0, rows: 30};

    constructor(
        public serviceInjector: Injector,
        public invoiceService: InvoiceService,
        private companyService: CompanyService,
    ) {
        super(serviceInjector);
        this.userIsAdmin = this.isSuperAdmin() ?? false;
        this.userIsAgent = this.isAgent() ?? false;
        this.overviewLoading = !this.userIsAdmin && !this.userIsAgent;
    }

    async ngOnInit() {
        this.userIsAdmin = this.isSuperAdmin() ?? false;
        this.userIsAgent = this.isAgent() ?? false;
        this.canFilterCompanies = !!this.hasPermission(Permissions.globalVisibility);
        this.buildPeriodOptions();

        if (this.isCompanyView) {
            this.selectedPeriod = moment().format('YYYY-MM-01');
            void this.loadPeriodOverview();
        }

        if (this.canFilterCompanies) {
            this.companies = (await this.companyService.getCompanies({$orderby: 'name asc'})) ?? [];
        }
    }

    get isCompanyView() {
        return !this.userIsAdmin && !this.userIsAgent;
    }

    get pageTitle() {
        return 'Report e fatture';
    }

    get pageDescription() {
        if (this.userIsAgent) {
            return 'Rendiconti di periodo e fatture fiscali relativi alla tua attività.';
        }
        if (this.userIsAdmin) {
            return 'Rendiconti di periodo e fatture fiscali emessi da Well Done verso le società.';
        }
        return 'Controlla gli incassi della società e i pagamenti a Well Done.';
    }

    get activeFilterCount(): number {
        return [
            !this.isCompanyView && this.selectedPeriod,
            this.selectedCompanyIds.length,
            this.selectedStatusIds.length,
            this.selectedMethodIds.length,
            this.sortBy !== 'createdAt desc',
        ].filter(Boolean).length;
    }

    get hasActiveQuery(): boolean {
        return !!this.textFilter.trim() || this.activeFilterCount > 0;
    }

    get columnCount(): number {
        return this.isCompanyView ? 6 : 7;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.filtersPanel?.overlayVisible) return;
        const target = event.target as HTMLElement | null;
        if (!target) return;
        if (
            target.closest('.user-filter-popover') ||
            target.closest('.user-filter-trigger') ||
            target.closest('.p-dropdown-panel') ||
            target.closest('.p-multiselect-panel')
        ) {
            return;
        }
        this.filtersPanel.hide();
    }

    async loadInvoices(event?: LazyLoadEvent) {
        if (event) {
            this.lastLazyEvent = event;
            this.first = event.first ?? 0;
            this.rows = event.rows ?? 30;
        }

        this.userIsAdmin = this.isSuperAdmin() ?? false;
        this.userIsAgent = this.isAgent() ?? false;

        const first = this.lastLazyEvent.first ?? 0;
        const rows = this.lastLazyEvent.rows ?? 30;
        const params: Record<string, string | number> = {
            $skip: first,
            $top: rows,
            type: 'wisper',
        };

        if (this.textFilter.trim()) params['text'] = this.textFilter.trim();
        if (this.selectedPeriod) params['date'] = this.selectedPeriod;
        if (this.canFilterCompanies && this.selectedCompanyIds.length) {
            params['companyId'] = this.selectedCompanyIds.join(',');
        }
        if (this.selectedStatusIds.length) params['paymentStatus'] = this.selectedStatusIds.join(',');
        if (this.selectedMethodIds.length) params['paymentMethod'] = this.selectedMethodIds.join(',');
        if (this.sortBy) params['$orderby'] = this.sortBy;

        if (!this.userIsAdmin && !this.userIsAgent) {
            params['companyId'] = localStorage.getItem('wisper_companyId') ?? '';
        } else if (this.userIsAgent) {
            params['agentId'] = this.getCurrentUser() ?? '';
        }

        this.loading = true;
        this.invoices = [];
        const response = await this.invoiceService.getInvoices(params);
        this.invoices = (response.data ?? [])
            .filter((item: any) => item?.number)
            .map((item: any) => new Invoice(item));
        this.totalRecords = response.count ?? this.invoices.length;
        this.loading = false;

        if (this.selectedInvoice?.id) {
            const updated = this.invoices.find(invoice => invoice.id === this.selectedInvoice?.id);
            if (updated) this.selectedInvoice = updated;
        }

        if (this.isCompanyView) {
            void this.loadPeriodOverview();
        }
    }

    async loadPeriodOverview() {
        if (!this.isCompanyView) return;
        const requestId = ++this.overviewRequestId;
        this.overviewLoading = true;
        const params: Record<string, string> = {};
        if (this.selectedPeriod) params['date'] = this.selectedPeriod;
        const companyId = localStorage.getItem('wisper_companyId');
        if (companyId) params['companyId'] = companyId;
        const response = await this.invoiceService.getPeriodOverview(params);
        if (requestId !== this.overviewRequestId) return;
        this.overview = response.success ? response.data ?? null : null;
        this.overviewLoading = false;
    }

    onOverviewMonthChange() {
        this.incomeRowsVisible = 25;
        if (this.activeCompanyTab === 'welldone') this.applyFilters();
        else void this.loadPeriodOverview();
    }

    selectCompanyTab(tab: 'income' | 'welldone') {
        this.filtersPanel?.hide();
        this.activeCompanyTab = tab;
        this.feeHelpOpen = false;
    }

    applyFilters() {
        this.first = 0;
        this.lastLazyEvent = {...this.lastLazyEvent, first: 0};
        this.filtersPanel?.hide();
        void this.loadInvoices(this.lastLazyEvent);
    }

    clearAdvancedFilters() {
        this.selectedPeriod = this.isCompanyView ? moment().format('YYYY-MM-01') : null;
        this.selectedCompanyIds = [];
        this.selectedStatusIds = [];
        this.selectedMethodIds = [];
        this.sortBy = 'createdAt desc';
        this.applyFilters();
    }

    resetAll() {
        this.textFilter = '';
        this.clearAdvancedFilters();
    }

    openInvoiceDetail(row: Invoice) {
        this.selectedInvoice = row;
        this.detailOpen = true;
    }

    async markAsPaid(invoiceId: string) {
        const confirmation = await Swal.fire({
            icon: 'warning',
            title: 'Segnare la fattura come pagata?',
            text: 'La fattura fiscale è già stata emessa, se disponibile. Il rendiconto resta invariato.',
            showCancelButton: true,
            confirmButtonText: 'Segna come pagata',
            cancelButtonText: 'Annulla',
        });
        if (!confirmation.isConfirmed) return;

        this.spinner.show();
        try {
            const result = await this.invoiceService.markAsPaid(invoiceId);
            if (result?.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Fattura segnata come pagata',
                    text: 'Lo stato di pagamento è stato aggiornato.',
                });
                await this.loadInvoices(this.lastLazyEvent);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: "Errore nell'operazione, riprova",
                });
            }
        } finally {
            this.spinner.hide();
        }
    }

    async regenerateReport(invoiceId: string) {
        const confirmation = await Swal.fire({
            icon: 'question',
            title: 'Rigenerare il rendiconto?',
            text: 'Il PDF del rendiconto verrà sostituito, senza riemettere la fattura fiscale.',
            showCancelButton: true,
            confirmButtonText: 'Rigenera rendiconto',
            cancelButtonText: 'Annulla',
        });
        if (!confirmation.isConfirmed) return;

        this.regeneratingInvoiceId = invoiceId;
        this.spinner.show();
        try {
            const result: any = await this.invoiceService.regenerateReport(invoiceId);
            if (!result?.success) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Impossibile rigenerare il rendiconto',
                    text: typeof result?.error === 'string'
                        ? result.error
                        : 'Riprova tra qualche minuto.',
                });
                return;
            }

            await this.loadInvoices(this.lastLazyEvent);
            const reportUrl = result.data?.url;
            const completed = await Swal.fire({
                icon: 'success',
                title: 'Rendiconto rigenerato',
                text: 'Il nuovo PDF è disponibile.',
                showCancelButton: Boolean(reportUrl),
                confirmButtonText: reportUrl ? 'Apri rendiconto' : 'Chiudi',
                cancelButtonText: 'Chiudi',
            });
            if (reportUrl && completed.isConfirmed) {
                window.open(reportUrl, '_blank', 'noopener,noreferrer');
            }
        } finally {
            this.regeneratingInvoiceId = undefined;
            this.spinner.hide();
        }
    }

    paymentStatusLabel(status?: string) {
        switch (status) {
            case 'paid': return 'Pagata';
            case 'waitingPayment': return 'In attesa';
            case 'notPaid': return 'Da pagare';
            default: return status || '—';
        }
    }

    paymentStatusBadgeClass(status?: string) {
        if (status === 'paid') return 'badge-success';
        if (status === 'waitingPayment') return 'badge-warning';
        if (status === 'notPaid') return 'badge-error';
        return 'badge-unknown';
    }

    paymentMethodLabel(method?: string) {
        return this.methodOptions.find(option => option.id === method)?.name || method || '—';
    }

    formatAmount(amount?: number) {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR',
        }).format(Number.isFinite(amount) ? Number(amount) : 0);
    }

    formatOverviewAmount(amount?: number | null) {
        return amount == null ? '—' : this.formatAmount(amount);
    }

    get showStripeForecast(): boolean {
        return !!this.overview?.projected && this.overview.subscriptionAmount == null;
    }

    get showStripePaymentCard(): boolean {
        return this.showStripeForecast || (
            !!this.overview?.invoice &&
            this.overview.subscriptionAmount === 0 &&
            (!!this.overview.nextStripePayment || this.overview.nextStripePaymentStatus === 'plan_missing')
        );
    }

    formatStripePaymentAmount(amount: number, currency: string) {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: currency || 'EUR',
        }).format(amount);
    }

    formatStripeOverviewAmount(amount?: number | null) {
        return amount == null ? '—' : this.formatStripePaymentAmount(amount, this.overview?.nextStripePayment?.currency || 'EUR');
    }

    formatDate(value?: string) {
        if (!value) return '—';
        const date = moment(value);
        return date.isValid() ? date.format('DD/MM/YYYY') : value;
    }

    formatPeriod(start?: string, end?: string) {
        return this.formatPeriodForTable(start ?? '', end ?? '');
    }

    feeAmount(invoice?: Invoice | null) {
        return Number(invoice?.transactionFeeAmount ?? invoice?.transactionFeeSummary?.feeAmount ?? 0);
    }

    transactionKindLabel(kind?: string) {
        return kind === 'order' ? 'Pagamento shop' : 'Iscrizione';
    }

    transactionPaymentLabel(row: InvoicePeriodTransaction) {
        if (row.kind === 'order') return 'Pagamento shop';
        if (row.installmentNumber && row.totalInstallments) {
            return `Rata iscrizione ${row.installmentNumber} di ${row.totalInstallments}`;
        }
        return 'Iscrizione';
    }

    feeRateLabel(rate?: number | null) {
        if (rate == null || Number.isNaN(Number(rate))) return '';
        return `${(Number(rate) * 100).toLocaleString('it-IT', {maximumFractionDigits: 2})} %`;
    }

    feeZeroLabel(row: InvoicePeriodTransaction) {
        switch (row.feeZeroReason) {
            case 'importo-zero':
                return 'Nessuna commissione su importo 0';
            case 'pending':
                return 'Calcolata quando il pagamento è confermato';
            case 'zero-rate':
                return 'Piano con commissione 0%';
            case 'none':
                return 'Nessuna commissione su questa transazione';
            default:
                return '';
        }
    }

    selectedPeriodLabel() {
        return this.periodOptions.find(option => option.id === this.selectedPeriod)?.name || 'Mese corrente';
    }

    hasReport(invoice?: Invoice | null) {
        return !!invoice?.url;
    }

    hasFiscalInvoice(invoice?: Invoice | null) {
        return !!invoice?.fattureInCloudDocumentUrl;
    }

    async openStripeInvoice(invoice: Invoice) {
        const documentWindow = window.open('', '_blank');
        const response = await this.invoiceService.getStripeInvoiceDocument(invoice.id);
        const url = response.data?.pdfUrl || response.data?.hostedUrl;
        if (response.success && url) {
            if (documentWindow) documentWindow.location.href = url;
            else window.location.href = url;
            return;
        }
        documentWindow?.close();
        await Swal.fire({
            icon: 'info',
            title: 'Fattura Stripe non disponibile',
            text: 'Il documento potrebbe non essere stato ancora emesso. Riprova più tardi.',
        });
    }

    copyValue(value: string | undefined, key: string) {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            this.copiedKey = key;
            if (this.copiedTimer) clearTimeout(this.copiedTimer);
            this.copiedTimer = setTimeout(() => {
                this.copiedKey = '';
                this.copiedTimer = null;
            }, 1400);
        });
    }

    private buildPeriodOptions() {
        const currentDate = moment().locale('it');
        this.periodOptions = [];
        for (let i = 0; i < 24; i++) {
            const month = currentDate.clone().subtract(i, 'months');
            const label = month.format('MMMM YYYY');
            this.periodOptions.push({
                id: month.format('YYYY-MM-01'),
                name: label.charAt(0).toUpperCase() + label.slice(1),
            });
        }
    }
}
